import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../../components/ui/sheet";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { FilterState, Requirement } from "../../types/deals";
import { Search, Filter, X, Download, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { RMList } from "../../utils/interfaces";
import SimpleDateRangePicker from "../SimpleDateRangePicker";


interface LenderTypes {
  id: number;
  name: string;
  display_order: number;
  parent_type: string;
};

interface LenderCompanyList {
  id: number;
  name: string;
}

interface DashboardFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  requirements: Requirement[];
  onExport?: () => void;
  uniqueRmNames?: RMList[];
  lender_user_product_types?: LenderTypes[]
  LenderStatus?: any,
  lenderCompanyList?: LenderCompanyList[]
}

export function DashboardFilters({ lenderCompanyList, lender_user_product_types, LenderStatus, filters, onFiltersChange, requirements, onExport, uniqueRmNames }: DashboardFiltersProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [rmOpen, setRmOpen] = useState(false);
  const [lenderInstOpen, setLenderInstOpen] = useState(false);

  // Count active filters
  const activeFilterCount =
    (filters.products.length > 0 ? 1 : 0) +
    (filters.statuses.length > 0 ? 1 : 0) +
    (filters.applicationRMs.length > 0 ? 1 : 0) +
    (filters.dateRange.from || filters.dateRange.to ? 1 : 0) +
    (filters.lenderInstitutions.length > 0 ? 1 : 0) +
    (filters.lenderNames.length > 0 ? 1 : 0) +
    (filters.requirementTypes?.length ?? 0 > 0 ? 1 : 0) +
    (filters.customerType.length > 0 ? 1 : 0) +
    (filters.showStagnant ? 1 : 0);

  function updateFilters(updates: Partial<FilterState>) {
    onFiltersChange({ ...filters, ...updates });
  }

  const dateRange = filters.dateRange.from || filters.dateRange.to ?
    { from: filters.dateRange.from, to: filters.dateRange.to } :
    undefined;



  function clearAllFilters() {
    onFiltersChange({
      search: "",
      products: [],
      statuses: [],
      applicationRMs: [],
      dateRange: {},
      lenderInstitutions: [],
      lenderNames: [],
      requirementTypes: [],
      showStagnant: false,
      customerType: [],
      currentPageFilter: "loggedIn"
    });
  }

  function toggleArrayFilter<T>(array: T[], value: T): T[] {
    return array.includes(value)
      ? array.filter(item => item !== value)
      : [...array, value];
  }

  // Cmd/Ctrl + K to focus search input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const input = document.getElementById("dashboard-filters-search-input") as HTMLInputElement | null;
        input?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="w-full min-w-0 space-y-4">
      {/* Search Bar */}
      <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
          <Input
            id="dashboard-filters-search-input"
            placeholder="Search by customer name or requirement ID..."
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
            className="h-9 w-full rounded-md border bg-background pl-9 pr-12 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          <kbd className="pointer-events-none absolute right-2 top-1/2 hidden h-5 -translate-y-1/2 items-center gap-0.5 rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground sm:inline-flex">
            ⌘K
          </kbd>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button
            onClick={onExport}
            variant="outline"
            className="shadow-sm transition-all duration-200 hover:shadow-md"
          >
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>

          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="flex items-center justify-between">
                  Filters
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      <X className="h-4 w-4 mr-1" />
                      Clear All
                    </Button>
                  )}
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-6 mt-6">
                {/* Quick Toggles */}
                <div>
                  <h3 className="font-medium mb-3">Quick Filters</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="hybrid"
                        checked={filters?.requirementTypes.includes("Hybrid")}
                        onCheckedChange={(checked) =>
                          updateFilters({
                            requirementTypes: checked
                              ? toggleArrayFilter(filters.requirementTypes, "Hybrid")
                              : filters?.requirementTypes.filter(t => t !== "Hybrid")
                          })
                        }
                      />
                      <label htmlFor="hybrid" className="text-sm">Hybrid</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="leasing"
                        checked={filters?.requirementTypes.includes("Leasing")}
                        onCheckedChange={(checked) =>
                          updateFilters({
                            requirementTypes: checked
                              ? toggleArrayFilter(filters?.requirementTypes, "Leasing")
                              : filters?.requirementTypes.filter(t => t !== "Leasing")
                          })
                        }
                      />
                      <label htmlFor="leasing" className="text-sm">Leasing</label>
                    </div>
                    {/* <div className="flex items-center space-x-2">
                      <Checkbox
                        id="stagnant"
                        checked={filters.showStagnant}
                        onCheckedChange={(checked) => updateFilters({ showStagnant: !!checked })}
                      />
                      <label htmlFor="stagnant" className="text-sm">Smart EPP</label>
                    </div> */}

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="Smart EPP"
                        checked={filters?.requirementTypes.includes("Smart EPP")}
                        onCheckedChange={(checked) =>
                          updateFilters({
                            requirementTypes: checked
                              ? toggleArrayFilter(filters.requirementTypes, "Smart EPP")
                              : filters?.requirementTypes.filter(t => t !== "Smart EPP")
                          })
                        }
                      />
                      <label htmlFor="Smart EPP" className="text-sm">Smart EPP</label>
                    </div>
                  </div>
                </div>

                {/* <div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="customer"
                        checked={filters?.customerType.includes("customer")}
                        onCheckedChange={(checked) =>
                          updateFilters({
                            customerType: checked
                              ? toggleArrayFilter(filters.customerType, "customer")
                              : filters?.customerType.filter(t => t !== "customer")
                          })
                        }
                      />
                      <label htmlFor="customer" className="text-sm">Customer</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="prospects"
                        checked={filters?.customerType.includes("prospects")}
                        onCheckedChange={(checked) =>
                          updateFilters({
                            customerType: checked
                              ? toggleArrayFilter(filters?.customerType, "prospects")
                              : filters?.customerType.filter(t => t !== "prospects")
                          })
                        }
                      />
                      <label htmlFor="Prospects" className="text-sm">Prospects</label>
                    </div>
                  </div>
                </div> */}

                {/* Lender Institution - Searchable Dropdown */}
                {lenderCompanyList?.length && lenderCompanyList?.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3">Lender Institution</h3>
                    <Popover open={lenderInstOpen} onOpenChange={setLenderInstOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={lenderInstOpen}
                          className="w-full justify-between"
                        >
                          {filters.lenderInstitutions.length > 0
                            ? `${filters.lenderInstitutions.length} selected`
                            : "Select institutions..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search institutions..." />
                          <CommandEmpty>No institution found.</CommandEmpty>
                          <CommandList>
                            <CommandGroup>
                              {lenderCompanyList?.map((institution) => (
                                <CommandItem
                                  key={institution.id}
                                  onSelect={() => {
                                    const isSelected = filters.lenderInstitutions.includes(String(institution.id));
                                    if (isSelected) {
                                      updateFilters({
                                        lenderInstitutions: filters.lenderInstitutions.filter(i => i !== String(institution.id)),
                                      });
                                    } else {
                                      updateFilters({
                                        lenderInstitutions: [...filters.lenderInstitutions, String(institution.id)],
                                      });
                                    }
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      filters.lenderInstitutions.includes(String(institution.id)) ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {institution.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {/* Lender Name - Searchable Dropdown */}
                {/* {uniqueLenderNames.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3">Lender Name</h3>
                    <Popover open={lenderNameOpen} onOpenChange={setLenderNameOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={lenderNameOpen}
                          className="w-full justify-between"
                        >
                          {filters.lenderNames.length > 0
                            ? `${filters.lenderNames.length} selected`
                            : "Select lenders..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search lenders..." />
                          <CommandEmpty>No lender found.</CommandEmpty>
                          <CommandList>
                            <CommandGroup>
                              {uniqueLenderNames.map((name) => (
                                <CommandItem
                                  key={name}
                                  onSelect={() => {
                                    const isSelected = filters.lenderNames.includes(name);
                                    if (isSelected) {
                                      updateFilters({ lenderNames: filters.lenderNames.filter(n => n !== name) });
                                    } else {
                                      updateFilters({ lenderNames: [...filters.lenderNames, name] });
                                    }
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      filters.lenderNames.includes(name) ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )} */}

                {/* Products - Searchable Dropdown */}
                <div>
                  <h3 className="font-medium mb-3">Products</h3>
                  <Popover open={productsOpen} onOpenChange={setProductsOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={productsOpen}
                        className="w-full justify-between"
                      >
                        {filters.products.length > 0
                          ? `${filters.products.length} selected`
                          : "Select products..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0 bg-white" align="start">
                      <Command>
                        <CommandInput placeholder="Search products..." />
                        <CommandEmpty>No product found.</CommandEmpty>
                        <CommandList>
                          <CommandGroup>
                            {lender_user_product_types?.map((product) => {
                              const isSelected = filters.products.includes(product?.id);
                              return (
                                <CommandItem
                                  key={product.id}
                                  onSelect={() => {
                                    if (isSelected) {
                                      updateFilters({
                                        products: filters.products.filter((p) => p !== product.id),
                                      });
                                    } else {
                                      updateFilters({
                                        products: [...filters.products, product.id],
                                      });
                                    }
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      isSelected ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {product.name}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>


                {/* Status - Searchable Dropdown */}
                {/* Status - Searchable Dropdown */}
                <div>
                  <h3 className="font-medium mb-3">Status</h3>
                  <Popover open={statusOpen} onOpenChange={setStatusOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={statusOpen}
                        className="w-full justify-between"
                      >
                        {filters.statuses.length > 0
                          ? `${filters.statuses.length} selected`
                          : "Select statuses..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search statuses..." />
                        <CommandEmpty>No status found.</CommandEmpty>
                        <CommandList>
                          <CommandGroup>
                            {LenderStatus?.map((status: any) => {
                              const isSelected = filters.statuses.includes(status.id);
                              return (
                                <CommandItem
                                  key={status.id}
                                  onSelect={() => {
                                    if (isSelected) {
                                      updateFilters({
                                        statuses: filters.statuses.filter((s) => s !== status.id),
                                      });
                                    } else {
                                      updateFilters({
                                        statuses: [...filters.statuses, status.id],
                                      });
                                    }
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      isSelected ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {status.name}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Application RM - Searchable Dropdown */}
                <div>
                  <h3 className="font-medium mb-3">Application RM</h3>
                  <Popover open={rmOpen} onOpenChange={setRmOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={rmOpen}
                        className="w-full justify-between"
                      >
                        {filters.applicationRMs.length > 0
                          ? `${filters.applicationRMs.length} selected`
                          : "Select RMs..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search RMs..." />
                        <CommandEmpty>No RM found.</CommandEmpty>
                        <CommandList>
                          <CommandGroup>
                            {uniqueRmNames?.map((rm) => (
                              <CommandItem
                                key={rm.id}
                                onSelect={() => {
                                  const isSelected = filters.applicationRMs.includes(String(rm.id));
                                  if (isSelected) {
                                    updateFilters({
                                      applicationRMs: filters.applicationRMs.filter((id) => String(id) !== String(rm.id)),
                                    });
                                  } else {
                                    updateFilters({
                                      applicationRMs: [...filters.applicationRMs, String(rm.id)],
                                    });
                                  }
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    filters.applicationRMs.includes(String(rm.id)) ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {rm.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                {/* Date Range */}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Created Between</label>
                  <SimpleDateRangePicker
                    dateRange={filters.dateRange}
                    onDateRangeChange={(date) => { updateFilters({ dateRange: { ...date } }) }}
                    className="w-full"
                  />
                </div>


              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Active Filter Tags */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.requirementTypes.map(type => (
            <Badge key={type} variant="secondary" className="gap-1">
              {type}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilters({
                  requirementTypes: filters.requirementTypes.filter(t => t !== type)
                })}
              />
            </Badge>
          ))}
          {filters.products.map(productId => {
            const product = lender_user_product_types?.find(p => p.id === productId);
            return (
              <Badge key={productId} variant="secondary" className="gap-1">
                {product ? product.name : productId}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() =>
                    updateFilters({
                      products: filters.products.filter(p => p !== productId),
                    })
                  }
                />
              </Badge>
            );
          })}

          {filters.statuses.map(statusId => {
            const status = LenderStatus.find((s: any) => s.id === statusId);
            return (
              <Badge key={statusId} variant="secondary" className="gap-1">
                {status ? status.name : statusId}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() =>
                    updateFilters({
                      statuses: filters.statuses.filter(s => s !== statusId),
                    })
                  }
                />
              </Badge>
            );
          })}
          {filters.showStagnant && (
            <Badge variant="secondary" className="gap-1">
              Smart EPP
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilters({ showStagnant: false })}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}